/**
 * What a business may use: the plan's limits and features from code (plans.ts), changed for every
 * business on that plan by the platform admin, then for one business by a per-business override.
 */
import { z } from "zod";
import { FEATURES, type Feature, PLAN_IDS, PLANS, type PlanId } from "./plans";

const limit = z.number().int().min(0).max(1_000_000).nullable();
const limitsPatch = z.object({ branches: limit, users: limit, items: limit }).partial().strict();
const features = z.array(z.enum(FEATURES)).max(FEATURES.length);

/** Platform-wide changes to one plan; anything left out keeps the code default. */
export const planOverrideSchema = z
  .object({
    limits: limitsPatch,
    smsPerMonth: z.number().int().min(0).max(100_000),
    features,
    selfServe: z.boolean(),
    name: z.object({ en: z.string().trim().min(2).max(40), bn: z.string().trim().min(2).max(40) }),
  })
  .partial()
  .strict();
export type PlanOverride = z.infer<typeof planOverrideSchema>;

export const platformPlansSchema = z.partialRecord(z.enum(PLAN_IDS), planOverrideSchema);
export type PlatformPlans = z.infer<typeof platformPlansSchema>;

/** Extra or fewer limits and features for one business, e.g. 5 branches on Starter for a trial. */
export const tenantOverrideSchema = z
  .object({
    limits: limitsPatch.default({}),
    addFeatures: features.default([]),
    removeFeatures: features.default([]),
    note: z.string().trim().min(5).max(300),
    /** After this time the override no longer applies; null keeps it until removed. */
    expiresAt: z.number().int().positive().nullable().default(null),
  })
  .strict();
export type TenantOverride = z.infer<typeof tenantOverrideSchema>;

export interface Entitlements {
  plan: PlanId;
  name: { en: string; bn: string };
  limits: { branches: number | null; users: number | null; items: number | null };
  smsPerMonth: number;
  features: Feature[];
  selfServe: boolean;
  /** Whether a per-business override is in force. */
  overridden: boolean;
}

/** The plan as every business on it sees it, after platform changes. */
export function planWithChanges(planId: PlanId, platform?: PlatformPlans | null) {
  const base = PLANS[planId];
  const change = platform?.[planId] ?? {};
  return {
    id: planId,
    name: change.name ?? base.name,
    limits: { ...base.limits, ...change.limits },
    smsPerMonth: change.smsPerMonth ?? base.smsPerMonth,
    features: [...(change.features ?? base.features)],
    selfServe: change.selfServe ?? base.selfServe,
  };
}

export function entitlementsFor(
  planId: PlanId,
  platform?: PlatformPlans | null,
  override?: Pick<TenantOverride, "limits" | "addFeatures" | "removeFeatures" | "expiresAt"> | null,
  now = Date.now(),
): Entitlements {
  const plan = planWithChanges(planId, platform);
  const active = override && (override.expiresAt === null || override.expiresAt > now);
  if (!active) return { plan: planId, ...withoutId(plan), overridden: false };
  const set = new Set<Feature>(plan.features);
  for (const f of override.addFeatures) set.add(f);
  for (const f of override.removeFeatures) set.delete(f);
  return {
    plan: planId,
    ...withoutId(plan),
    limits: { ...plan.limits, ...override.limits },
    features: FEATURES.filter((f) => set.has(f)),
    overridden: true,
  };
}

function withoutId(plan: ReturnType<typeof planWithChanges>) {
  const { id: _id, ...rest } = plan;
  return rest;
}
